import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import {
    confirmConfigChange,
    fetchConfigOverrides,
    fetchEditableConfigSpec,
    fetchEffectiveConfig,
    stageConfigChange,
    type ConfigOverrideMeta,
    type EditableConfigSpec,
} from '@/api/console';
import {
    Button,
    Callout,
    EmptyState,
    ErrorState,
    HoverCard,
    Input,
    Select,
    SkeletonTable,
} from '@/components/ui';
import { displayValue, durationUnit, formatDuration, settingLabel } from './configDisplay';
import { parseValue, toEditorText } from './configValues';

/**
 * EAA-1823: the BE-backed half of `/console/config` — the config actually in
 * force (`GET /admin/config`), with a two-step confirmed edit for the keys the
 * server declares editable.
 *
 * Why two steps rather than a plain save: a confirmed change takes effect on the
 * running service immediately (see `RuntimeConfigService.get_effective`), so the
 * flow deliberately makes the operator state the intent twice. Staging alone
 * writes nothing that any consumer reads — abandoning the form leaves the
 * service exactly as it was.
 *
 * The confirmation token is revealed exactly once, by the stage response. It
 * lives in component state for the few seconds between the two calls and is
 * never logged, never put in a react-query key, and never persisted — a token
 * in a cache key would end up in devtools and survive the flow it belongs to.
 */

/** Nothing in flight, or one key mid-edit / awaiting its confirmation. */
type EditState =
    | { phase: 'idle' }
    | { phase: 'editing'; key: string; draft: string }
    | { phase: 'confirming'; key: string; changeId: string; token: string; pending: unknown };

interface EffectiveConfigPanelProps {
    /** Filter text from the page header (frame 64 puts the box beside the title). */
    filter?: string;
    onClearFilter?: () => void;
}

export function EffectiveConfigPanel({
    // Defaulted even though the prop is required: during a hot reload the browser
    // can hold this module against the previous ConfigPage, and an unfiltered
    // table is a better answer to that than a white screen on an admin page.
    filter = '',
    onClearFilter = () => {},
}: EffectiveConfigPanelProps) {
    const can = useCan();
    const { t, i18n } = useTranslation();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const queryClient = useQueryClient();

    const [edit, setEdit] = useState<EditState>({ phase: 'idle' });
    const [formError, setFormError] = useState<string | null>(null);

    const canView = can('console.config.view');
    const canManage = can('console.config.manage');
    const enabled = authenticated && Boolean(accessToken) && canView;

    const configQuery = useQuery({
        queryKey: ['admin-config', accessToken],
        queryFn: () => fetchEffectiveConfig(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });

    // Fetched only when the operator could act on it — a viewer without
    // `console.config.manage` never renders an editor, so the spec is dead weight.
    const specQuery = useQuery({
        queryKey: ['admin-config-editable', accessToken],
        queryFn: () => fetchEditableConfigSpec(config.apiBase, accessToken as string),
        enabled: enabled && canManage,
        retry: false,
    });

    // Provenance (default value, who changed it, when). Available to viewers too —
    // "this is not the shipped default" is part of reading the config honestly, not
    // a privilege of the people who can change it.
    const overridesQuery = useQuery({
        queryKey: ['admin-config-overrides', accessToken],
        queryFn: () => fetchConfigOverrides(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });

    const reset = () => {
        setEdit({ phase: 'idle' });
        setFormError(null);
    };

    const stageMutation = useMutation({
        mutationFn: (vars: { key: string; value: unknown }) =>
            stageConfigChange(config.apiBase, accessToken as string, vars.key, vars.value),
        onSuccess: (staged, vars) => {
            setFormError(null);
            setEdit({
                phase: 'confirming',
                key: vars.key,
                changeId: staged.id,
                token: staged.token,
                pending: vars.value,
            });
        },
        onError: (error: Error) => setFormError(error.message),
    });

    const confirmMutation = useMutation({
        mutationFn: (vars: { changeId: string; token: string }) =>
            confirmConfigChange(config.apiBase, accessToken as string, vars.changeId, vars.token),
        onSuccess: async () => {
            reset();
            // The staged value is now the effective one; refetch rather than
            // patching the cache, so what is shown is what the server reports.
            // Provenance moves with it — the row just became overridden, by us, now.
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['admin-config'] }),
                queryClient.invalidateQueries({ queryKey: ['admin-config-overrides'] }),
            ]);
        },
        onError: (error: Error) => setFormError(error.message),
    });

    if (!canView) {
        return null;
    }

    if (configQuery.isPending) {
        return <SkeletonTable columns={3} />;
    }

    if (configQuery.isError) {
        return (
            <ErrorState
                title={t('console.config.effectiveLoadError')}
                onRetry={() => void configQuery.refetch()}
            />
        );
    }

    const values = configQuery.data ?? {};
    const specs = specQuery.data ?? {};
    const overrides = overridesQuery.data ?? {};

    // Three bands, in the order an operator cares about them:
    //   1. changed away from the default — the rows that explain current behaviour
    //   2. editable but still default   — the rows they came here to change
    //   3. read-only                    — reference
    // Within a band, by DISPLAYED name: the table is read in Vietnamese, so
    // ordering by the English identifier would look arbitrary.
    const band = (key: string) => {
        if (!specs[key]) return 2;
        return overrides[key]?.overridden ? 0 : 1;
    };
    const allKeys = Object.keys(values).sort((a, b) => {
        if (band(a) !== band(b)) return band(a) - band(b);
        return settingLabel(a, t).localeCompare(settingLabel(b, t), i18n.language);
    });

    // Filter on the name AND the raw key: an operator who knows the setting name
    // from settings.py must be able to find it even though the key is now tucked
    // into the tooltip.
    const needle = filter.trim().toLowerCase();
    const keys = needle
        ? allKeys.filter(
              (key) =>
                  key.toLowerCase().includes(needle) ||
                  settingLabel(key, t).toLowerCase().includes(needle)
          )
        : allKeys;

    const editableCount = keys.filter((key) => specs[key]).length;
    const lastChangedAt = Object.values(overrides)
        .map((meta) => meta.updated_at)
        .filter(Boolean)
        .sort()
        .pop();

    const beginEdit = (key: string) => {
        setFormError(null);
        setEdit({ phase: 'editing', key, draft: toEditorText(values[key]) });
    };

    const submitStage = (key: string, spec: EditableConfigSpec, draft: string) => {
        const parsed = parseValue(spec, draft);
        if (!parsed.ok) {
            setFormError(t(`console.config.parseError.${parsed.error}`));
            return;
        }
        stageMutation.mutate({ key, value: parsed.value });
    };

    const renderEditor = (key: string, spec: EditableConfigSpec, draft: string) => {
        const setDraft = (next: string) => setEdit({ phase: 'editing', key, draft: next });

        if (spec.type === 'bool') {
            return (
                <Select
                    value={draft === 'true' ? 'true' : 'false'}
                    onChange={(next: string) => setDraft(next)}
                    options={[
                        { label: t('console.config.boolTrue'), value: 'true' },
                        { label: t('console.config.boolFalse'), value: 'false' },
                    ]}
                    style={{ minWidth: 160 }}
                />
            );
        }

        if (spec.type === 'list') {
            return (
                <Input.TextArea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={4}
                    placeholder={t('console.config.listPlaceholder')}
                />
            );
        }

        const unit = durationUnit(key);
        const parsed = parseValue(spec, draft);
        return (
            <span className="portal-console-config-editor">
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    inputMode={spec.type === 'int' ? 'numeric' : undefined}
                    // A number field is a number field: frame 64 sizes it to its
                    // content instead of letting it span the column. A URL needs
                    // the room, so only the int case is narrowed.
                    className={
                        spec.type === 'int' ? 'portal-console-config-input-narrow' : undefined
                    }
                />
                {/* Live readback of what the typed number means, so "1800" is
                    confirmed as half an hour before it is staged, not after. */}
                {unit && parsed.ok && typeof parsed.value === 'number' && (
                    <span className="portal-console-config-unit">
                        {t(`console.config.unit.${unit}`).toLowerCase()}
                        {' · '}
                        {formatDuration(parsed.value, unit, t)}
                    </span>
                )}
            </span>
        );
    };

    /** The bounds line under an editor, so the server's rules are visible up front. */
    const renderHint = (spec: EditableConfigSpec) => {
        const parts: string[] = [];
        if (spec.min !== undefined && spec.max !== undefined) {
            parts.push(t('console.config.hintRange', { min: spec.min, max: spec.max }));
        }
        if (spec.max_length !== undefined) {
            parts.push(t('console.config.hintMaxLength', { max: spec.max_length }));
        }
        if (spec.format === 'url') parts.push(t('console.config.hintUrl'));
        if (spec.item_format === 'origin') parts.push(t('console.config.hintOrigin'));
        if (spec.max_items !== undefined) {
            parts.push(t('console.config.hintMaxItems', { max: spec.max_items }));
        }
        return parts.length > 0 ? <p className="portal-admin-hint">{parts.join(' · ')}</p> : null;
    };

    /**
     * The ⓘ beside a name. Holds what the row cannot show inline without turning
     * back into a wall of identifiers: the raw settings key, what it does, and the
     * bounds the server will enforce.
     */
    const renderKeyInfo = (key: string, spec: EditableConfigSpec | undefined) => {
        const description = t(`console.config.descriptions.${key}`, { defaultValue: '' });
        const meta: string[] = [];
        const unit = durationUnit(key);
        if (unit) meta.push(t(`console.config.unit.${unit}`));
        const fallback = overrides[key]?.default;
        if (fallback !== undefined && fallback !== null && fallback !== '') {
            meta.push(t('console.config.metaDefault', { value: displayValue(key, fallback, t) }));
        }
        if (spec?.min !== undefined && spec?.max !== undefined) {
            meta.push(t('console.config.hintRange', { min: spec.min, max: spec.max }));
        }

        return (
            <HoverCard
                placement="right"
                trigger={
                    // An inline SVG, not the "ⓘ" character: that glyph is missing
                    // from the Nunito subsets this app ships and rendered as tofu.
                    <span className="portal-console-config-info" aria-label={key} role="img">
                        <svg
                            viewBox="0 0 24 24"
                            width="15"
                            height="15"
                            fill="none"
                            aria-hidden="true"
                        >
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                            <path
                                d="M12 11v5M12 7.6h.01"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                            />
                        </svg>
                    </span>
                }
                content={
                    <div className="portal-console-config-tip">
                        <code>{key}</code>
                        {description && <p>{description}</p>}
                        {meta.length > 0 && <span>{meta.join(' · ')}</span>}
                    </div>
                }
            />
        );
    };

    return (
        <>
            {/* Frame 64 draws this as a quiet one-liner with an icon, not a
                labelled banner — it is orientation, not a warning. */}

            {formError && (
                <Callout tone="danger" label={t('console.config.changeRefused')} longLabel>
                    {formError}
                </Callout>
            )}

            <table className="portal-admin-table portal-console-config-table">
                <thead>
                    <tr>
                        <th>{t('console.config.columnKey')}</th>
                        <th>{t('console.config.columnValue')}</th>
                        <th>{t('console.config.columnAction')}</th>
                    </tr>
                </thead>
                <tbody>
                    {keys.map((key, index) => {
                        const spec = specs[key];
                        const isEditing = edit.phase === 'editing' && edit.key === key;
                        const isConfirming = edit.phase === 'confirming' && edit.key === key;
                        // Blocked only once a change is STAGED: at that point moving to another
                        // row would orphan a change the operator never confirmed. While merely
                        // editing, nothing is staged, so switching rows costs nothing and the
                        // table stays live the way frame 64 draws it.
                        const otherInFlight = edit.phase === 'confirming' && !isConfirming;

                        // The first read-only row carries the divider that closes
                        // the editable group, so the split is visible without a
                        // second table or a repeated header.
                        const startsReadOnly = !spec && index === editableCount;
                        const meta: ConfigOverrideMeta | undefined = overrides[key];
                        const isOverridden = Boolean(meta?.overridden);

                        const rowClass = [
                            startsReadOnly ? 'portal-console-config-group-start' : '',
                            isOverridden ? 'portal-console-config-row-overridden' : '',
                            isEditing || isConfirming ? 'portal-console-config-row-active' : '',
                        ]
                            .filter(Boolean)
                            .join(' ');

                        return (
                            <tr key={key} className={rowClass || undefined}>
                                <td>
                                    <span className="portal-console-config-name">
                                        {settingLabel(key, t)}
                                        {renderKeyInfo(key, spec)}
                                    </span>
                                </td>
                                <td>
                                    {isEditing ? (
                                        <>
                                            {renderEditor(key, spec, edit.draft)}
                                            {renderHint(spec)}
                                        </>
                                    ) : isConfirming ? (
                                        <Callout
                                            tone="warning"
                                            label={t('console.config.pendingLabel')}
                                            longLabel
                                        >
                                            {t('console.config.pendingBody', {
                                                from: displayValue(key, values[key], t),
                                                to: displayValue(key, edit.pending, t),
                                            })}
                                        </Callout>
                                    ) : (
                                        <>
                                            <span className="portal-console-config-value">
                                                {displayValue(key, values[key], t)}
                                            </span>
                                            {/* Only for changed rows: a default that
                                                equals the value tells nobody anything. */}
                                            {isOverridden && meta && (
                                                <span className="portal-console-config-provenance">
                                                    {t('console.config.changedFrom', {
                                                        value: displayValue(key, meta.default, t),
                                                        by:
                                                            meta.updated_by ||
                                                            t('console.config.unknownActor'),
                                                        when: meta.updated_at
                                                            ? new Date(
                                                                  meta.updated_at
                                                              ).toLocaleString(i18n.language)
                                                            : '',
                                                    })}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </td>
                                <td>
                                    {isEditing ? (
                                        // Frame 64's order: the way out sits left of
                                        // the commit, so the primary is the rightmost
                                        // thing in the row.
                                        <div className="portal-console-config-actions">
                                            <Button size="small" onClick={reset}>
                                                {t('console.config.cancelAction')}
                                            </Button>
                                            <Button
                                                size="small"
                                                type="primary"
                                                loading={stageMutation.isPending}
                                                onClick={() => submitStage(key, spec, edit.draft)}
                                            >
                                                {t('console.config.stageAction')}
                                            </Button>
                                        </div>
                                    ) : isConfirming ? (
                                        <div className="portal-console-config-actions">
                                            <Button size="small" onClick={reset}>
                                                {t('console.config.cancelAction')}
                                            </Button>
                                            <Button
                                                size="small"
                                                type="primary"
                                                danger
                                                loading={confirmMutation.isPending}
                                                onClick={() =>
                                                    confirmMutation.mutate({
                                                        changeId: edit.changeId,
                                                        token: edit.token,
                                                    })
                                                }
                                            >
                                                {t('console.config.confirmAction')}
                                            </Button>
                                        </div>
                                    ) : spec && canManage ? (
                                        <Button
                                            size="small"
                                            disabled={otherInFlight}
                                            onClick={() => beginEdit(key)}
                                        >
                                            {t('console.config.editAction')}
                                        </Button>
                                    ) : (
                                        <span className="portal-admin-muted">
                                            {t('console.config.notEditable')}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {keys.length === 0 && (
                <EmptyState
                    title={t('console.config.noMatches', { filter: filter.trim() })}
                    action={
                        <Button onClick={onClearFilter}>{t('console.config.clearFilter')}</Button>
                    }
                />
            )}

            <p className="portal-console-config-footer">
                {t('console.config.footerCount', { count: allKeys.length })}
                {lastChangedAt &&
                    ` · ${t('console.config.footerLastChanged', {
                        when: new Date(lastChangedAt).toLocaleString(i18n.language),
                    })}`}
            </p>
        </>
    );
}
